const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// Scroll-in animation logic
function onIntersection(entries, observer) {
  entries.forEach((entry, index) => {
    const elementTarget = entry.target;

    if (entry.isIntersecting) {
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade')) {
          elementTarget.style.setProperty('--animation-order', index);
        }
      }
      observer.unobserve(elementTarget);
    } else {
      elementTarget.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      elementTarget.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => element.classList.add('scroll-trigger--design-mode'));
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });

  animationTriggerElements.forEach((element) => observer.observe(element));
}

// Zoom-in animation logic. One observer and one rAF-scheduled event handler are shared
// by all zoom elements instead of creating one scroll listener per element.
const zoomElements = new Set();
const visibleZoomElements = new Set();
let zoomObserver;
let zoomAnimationFrame;
const ZOOM_SCALE_AMOUNT = 0.2 / 100;

function updateZoomElements() {
  zoomAnimationFrame = undefined;
  visibleZoomElements.forEach((element) => {
    if (!element.isConnected) {
      visibleZoomElements.delete(element);
      zoomElements.delete(element);
      return;
    }

    element.style.setProperty('--zoom-in-ratio', 1 + ZOOM_SCALE_AMOUNT * percentageSeen(element));
  });
}

function scheduleZoomUpdate() {
  if (zoomAnimationFrame !== undefined) return;
  zoomAnimationFrame = window.requestAnimationFrame(updateZoomElements);
}

function getZoomObserver() {
  if (zoomObserver) return zoomObserver;

  zoomObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        visibleZoomElements.add(entry.target);
      } else {
        visibleZoomElements.delete(entry.target);
      }
    });
    scheduleZoomUpdate();
  });

  return zoomObserver;
}

function initializeScrollZoomAnimationTrigger(rootEl = document) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  const observer = getZoomObserver();
  animationTriggerElements.forEach((element) => {
    if (zoomElements.has(element)) return;
    zoomElements.add(element);
    element.style.setProperty('--zoom-in-ratio', 1 + ZOOM_SCALE_AMOUNT * percentageSeen(element));
    observer.observe(element);
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) return 0;
  if (elementPositionY + elementHeight < scrollY) return 100;

  const distance = scrollY + viewportHeight - elementPositionY;
  const percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});
window.addEventListener('scroll', scheduleZoomUpdate, { passive: true });
window.addEventListener('resize', scheduleZoomUpdate, { passive: true });

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    initializeScrollAnimationTrigger(event.target, true);
    initializeScrollZoomAnimationTrigger(event.target);
  });
  document.addEventListener('shopify:section:reorder', () => {
    initializeScrollAnimationTrigger(document, true);
    initializeScrollZoomAnimationTrigger(document);
  });
}
